import { useEffect, useRef } from 'react';

const awards = [
  {
    icon: 'ri-trophy-line',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    accentBorder: true,
    title: '2019 Travelers\' Choice Award',
    year: '2019',
    description: 'TripAdvisor\'s highest recognition, awarded exclusively to properties ranked in the top 1% of accommodations worldwide.',
    badge: 'Top 1% Worldwide',
  },
  {
    icon: 'ri-medal-line',
    iconBg: 'bg-forest-50',
    iconColor: 'text-forest',
    accentBorder: false,
    title: '#1 Bed & Breakfast in Matagalpa',
    year: 'TripAdvisor',
    description: 'Consistently ranked as the top-rated bed and breakfast in Matagalpa, Nicaragua, on TripAdvisor.',
    badge: '#1 Ranked',
  },
  {
    icon: 'ri-star-fill',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    accentBorder: false,
    title: '5-Star TripAdvisor Rating',
    year: 'TripAdvisor',
    description: 'A perfect 5-star rating from hundreds of verified guest reviews, reflecting our unwavering commitment to excellence.',
    badge: '5 Stars',
  },
  {
    icon: 'ri-honour-line',
    iconBg: 'bg-forest-50',
    iconColor: 'text-forest',
    accentBorder: false,
    title: 'Certificate of Excellence',
    year: 'Multiple Years',
    description: 'Recipient of TripAdvisor\'s prestigious Certificate of Excellence for multiple consecutive years, recognizing outstanding service.',
    badge: 'Multiple Years',
  },
  {
    icon: 'ri-leaf-line',
    iconBg: 'bg-green-50',
    iconColor: 'text-green-700',
    accentBorder: false,
    title: 'GreenLeaders Silver Certification',
    year: 'TripAdvisor',
    description: 'Recognized for meaningful eco-friendly and sustainable practices with TripAdvisor\'s GreenLeaders Silver Level certification.',
    badge: 'Silver Level',
  },
];

export default function AwardsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('is-visible');
      }),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="awards" ref={sectionRef} className="py-20 md:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">

          {/* Left — Title */}
          <div className="w-full lg:w-[38%] lg:sticky lg:top-28 reveal">
            <div className="inline-flex items-center gap-2 bg-gold/10 text-gold rounded-full px-4 py-1.5 mb-5">
              <i className="ri-award-line text-sm w-4 h-4 flex items-center justify-center" />
              <span className="font-sans text-xs tracking-widest uppercase">Awards &amp; Certifications</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-forest-dark leading-tight mb-6">
              Recognized<br /><em>Excellence</em>
            </h2>
            <p className="font-sans text-base text-gray-600 leading-relaxed mb-8">
              Montebrisa has earned international recognition for the quality of its accommodations, service, and commitment to sustainability — standing among the world&apos;s finest boutique properties.
            </p>

            {/* Hero stat */}
            <div className="bg-white rounded-2xl p-6 border border-cream-dark">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-xl shrink-0">
                  <i className="ri-trophy-fill text-amber-500 text-2xl" />
                </div>
                <div>
                  <p className="font-serif text-forest-dark text-lg font-medium">Top 1% Worldwide</p>
                  <p className="font-sans text-xs text-gray-500 mt-1">2019 TripAdvisor Travelers&apos; Choice — Highest Recognition</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Awards list */}
          <div className="w-full lg:w-[62%] flex flex-col gap-4">
            {awards.map((award, idx) => (
              <div
                key={award.title}
                className={`reveal bg-white rounded-xl p-5 border transition-all duration-300 hover:-translate-y-0.5 ${
                  award.accentBorder ? 'border-gold/50' : 'border-cream-dark'
                }`}
                style={{ transitionDelay: `${idx * 0.08}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 flex items-center justify-center ${award.iconBg} rounded-xl shrink-0`}>
                    <i className={`${award.icon} ${award.iconColor} text-lg`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <h3 className="font-sans font-semibold text-forest-dark text-sm">{award.title}</h3>
                      <span className={`text-xs font-sans rounded-full px-2.5 py-0.5 shrink-0 whitespace-nowrap ${
                        award.accentBorder
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-forest-50 text-forest'
                      }`}>
                        {award.badge}
                      </span>
                    </div>
                    <p className="font-sans text-xs text-gold mt-0.5 mb-2">{award.year}</p>
                    <p className="font-sans text-xs text-gray-500 leading-relaxed">{award.description}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* TripAdvisor mention */}
            <div className="reveal bg-forest rounded-xl p-5 text-center" style={{ transitionDelay: '0.4s' }}>
              <p className="font-sans text-white/80 text-sm">
                Visit <a href="https://www.Montebrisa.com" target="_blank" rel="nofollow noopener noreferrer" className="text-gold underline underline-offset-2 hover:text-gold-light">www.Montebrisa.com</a> for full photo gallery and verified guest reviews on TripAdvisor.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
